import { cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

// Base button styles shared across all button components
const baseButtonStyles = 'w-fit h-fit inline-flex items-center justify-center gap-2 border border-transparent outline-none transition-colors duration-200 focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-focus shrink-0'

// Common state variants
const stateVariants = {
    disabled: { true: 'cursor-not-allowed focus:ring-0 focus:ring-offset-0' },
    loading: { true: 'cursor-progress focus:ring-0 focus:ring-offset-0' },
    danger: { true: 'focus:ring-0 focus:ring-offset-0' },
}

// Define the common variant types
type CommonVariants = {
    variant: 'primary' | 'secondary' | 'tertiary'
    danger: boolean
    loading: boolean
    disabled: boolean
}

// Common compound variants for both standard and icon buttons
const commonCompoundVariants: {
    variant?: CommonVariants['variant']
    danger?: boolean
    loading?: boolean
    disabled?: boolean
    class: string
}[] = [
    // Base variants (without danger, loading or disabled)
    { variant: 'primary', danger: false, loading: false, disabled: false, class: 'bg-cta text-primary-inverse hover:bg-cta-hover active:bg-cta-active' },
    { variant: 'secondary', danger: false, loading: false, disabled: false, class: 'text-cta border border-focus hover:text-cta-hover hover:border-cta-hover hover:bg-cta-ghost active:bg-cta-ghost active:border-focus active:text-cta focus-visible:bg-cta-ghost' },
    { variant: 'tertiary', danger: false, loading: false, disabled: false, class: 'text-primary hover:bg-primary-ghost active:text-secondary focus-visible:bg-primary-ghost' },

    // Danger variants
    { variant: 'primary', danger: true, loading: false, disabled: false, class: 'bg-error-inverse text-primary-inverse' },
    { variant: 'secondary', danger: true, loading: false, disabled: false, class: 'text-error-core border border-error' },
    { variant: 'tertiary', danger: true, loading: false, disabled: false, class: 'text-error-core border border-primary' },

    // Loading variants
    { variant: 'primary', loading: true, class: 'bg-cta-inverse-hover text-primary-inverse' },
    { variant: 'secondary', loading: true, class: 'text-cta-inverse-hover border border-tertiary-inverse-hover' },
    { variant: 'tertiary', loading: true, class: 'text-disabled border border-primary' },

    // Disabled variants
    { variant: 'primary', disabled: true, class: 'bg-disabled text-disabled' },
    { variant: 'secondary', disabled: true, class: 'text-disabled border border-primary bg-disabled-ghost' },
    { variant: 'tertiary', disabled: true, class: 'text-disabled border border-primary' },
]

// Standard button variants
export const standardButtonVariants = cva(
    twMerge(baseButtonStyles, 'rounded-2'),
    {
        variants: {
            variant: {
                primary: '',
                secondary: '',
                tertiary: '',
                text: '',
            },
            size: {
                sm: 'px-2 h-6 paragraph-sm-medium',
                md: 'px-2 h-8 paragraph-sm-medium',
                lg: 'px-3 h-10 paragraph-md-medium',
            },
            ...stateVariants,
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
        },
        compoundVariants: [
            ...commonCompoundVariants,
            // Text variant specific compounds
            { variant: 'text', danger: false, loading: false, disabled: false, class: 'text-primary hover:text-secondary active:bg-primary-ghost active:text-secondary' },
            { variant: 'text', danger: true, loading: false, disabled: false, class: 'text-error-core' },
            { variant: 'text', loading: true, class: 'text-disabled' },
            { variant: 'text', disabled: true, class: 'text-disabled' },
        ],
    },
)

export const iconButtonVariants = cva(
    baseButtonStyles,
    {
        variants: {
            variant: {
                primary: '',
                secondary: '',
                tertiary: '',
                ghost: '',
            },
            size: {
                xs: 'w-6 h-6 rounded-1', // deprecated, use sm
                sm: 'w-6 h-6 rounded-1',
                md: 'w-8 h-8 rounded-2',
                lg: 'w-10 h-10 rounded-2',
            },
            ...stateVariants,
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
        },
        compoundVariants: commonCompoundVariants,
    },
)

// Link button variants
export const linkButtonVariants = cva(
    twMerge(baseButtonStyles, 'text-cta hover:text-cta-hover active:text-cta-active'),
    {
        variants: {
            size: {
                sm: 'paragraph-sm-underline',
                md: 'paragraph-sm-underline',
                lg: 'paragraph-md-underline',
            },
            disabled: { true: 'text-disabled cursor-not-allowed focus:ring-0 focus:ring-offset-0' },
        },
        defaultVariants: {
            size: 'md',
        },
    },
)
